import { datadogRum } from '@datadog/browser-rum';
import { toast } from '@side/pantry';
import { useEffect, useMemo } from 'react';
import { useInteractionDocuments } from '@/common/hooks/useInteractionDocuments/useInteractionDocuments';
import { InteractionType } from '@/common/types/interaction';
import { useCore } from '$api-clients/hooks';
import { buildOfferEnvelope } from '../../views/SentEvelopesSection/buildEnvelopes';
import { EnvelopeStatus } from '../../views/SentEvelopesSection/enums';
import { isSentStatus } from '../../views/SentEvelopesSection/Polling/sentEnvelopesPolling.helpers';

interface Props {
  id: string;
  type: InteractionType;
  open: boolean;
}

export const useGetOfferEnvelopes = ({ id, type, open }: Props) => {
  const shouldFetchData = Boolean(
    id &&
      open &&
      (type === InteractionType.offer || type === InteractionType.lease),
  );

  const {
    data: envelopes,
    isLoading: envelopesLoading,
    error: envelopesError,
  } = useCore(
    '/interactions/{interactionId}/envelopes',
    shouldFetchData ? { params: { path: { interactionId: id } } } : null,
  );

  const {
    documents,
    isLoading: documentsLoading,
    error: documentsError,
  } = useInteractionDocuments(id, { skip: !shouldFetchData });

  // Envelopes and documents load in parallel; surface a single toast if either fails.
  const error = envelopesError ?? documentsError;
  useEffect(() => {
    if (error !== undefined) {
      datadogRum.addError(error, { message: 'Error fetching offer envelopes' });
      toast('error', 'Error fetching offer envelopes', {
        showCloseButton: true,
      });
    }
  }, [error]);

  const envelopeList = useMemo(
    () =>
      (envelopes ?? [])
        .filter((envelope) => isSentStatus(envelope.status))
        .map((envelope) => buildOfferEnvelope(envelope, documents))
        .filter((envelope) => envelope.status !== EnvelopeStatus.voided),
    [envelopes, documents],
  );

  return {
    envelopes: envelopeList,
    loading: envelopesLoading || documentsLoading,
  };
};
